<script setup lang="ts">
import type { File, ProductLine, ProductWithImage } from '~/types/api'

type ProductImage = {
  product?: ProductWithImage
  image: File
  productLine: ProductLine
}

const props = defineProps<{
  productImages: ProductImage[]
  showLabel: boolean
  centerSingleImage?: boolean
}>()

const nid = computed(() => {
  return props.productImages[0]?.productLine?.nid
})

const localePath = useLocalePath()
const { getResourceUrl } = useResources()
</script>

<template>
  <UiCarousel
    :opts="{
      loop: productImages.length > 2,
      align: 'start',
    }"
    class="max-w-[640px]"
    :class="{
      '!max-w-80': centerSingleImage && productImages.length < 2,
    }"
  >
    <UiCarouselPrevious
      v-if="productImages.length > 2"
      class="absolute left-2 top-1/2 z-10"
    >
      <Icon size="30" name="bi:arrow-left" />
    </UiCarouselPrevious>
    <UiCarouselContent>
      <template v-for="productImage in productImages">
        <UiCarouselItem
          v-if="productImage.product?.suppressImagesInSet === 0"
          :key="productImage.image.nid"
          class="basis-1/2"
          :class="{
            'basis-full': centerSingleImage && productImages.length < 2,
          }"
        >
          <div class="mx-auto h-full" :class="'product-carousel-' + nid">
            <div class="flex h-full flex-col align-middle">
              <NuxtLink
                :to="
                  localePath({
                    name: 'line-slug',
                    params: {
                      line: productImage.productLine?.urlSlug,
                      slug: productImage.product?.productSetCode,
                    },
                  })
                "
                class="relative flex h-full flex-col justify-between no-underline"
              >
                <div class="h-full max-h-80">
                  <img
                    class="left-0 top-0 block h-full w-full object-cover"
                    :src="
                      getResourceUrl(productImage?.image?.url + '/576x576/FIT')
                    "
                    :alt="productImage?.image?.metaAlt ?? undefined"
                    :title="productImage?.image?.metaTitle ?? undefined"
                  />
                </div>
                <div v-if="showLabel" class="font-bold">
                  {{ productImage?.productLine?.title }}
                  {{ productImage?.product?.productSetCode }}
                </div>
              </NuxtLink>
            </div>
          </div>
        </UiCarouselItem>
      </template>
    </UiCarouselContent>
    <UiCarouselNext
      v-if="productImages.length > 2"
      class="absolute right-2 top-1/2 z-10"
    >
      <Icon size="30" name="bi:arrow-right" />
    </UiCarouselNext>
  </UiCarousel>
</template>

<style>
.product-carousel-control [aria-disabled='true'] {
  visibility: hidden;
}
</style>
